import round from 'lodash/round';

import { Box, Icons, tokens } from '@cast/design-system';
import { FailedToLoad } from '@cast/shared-messages-ui';
import { ComputeResourceType } from '@cast/shared-types';
import { dashesOrNum } from '@cast/utils';

import {
  computeResourceUnitsMap,
  computeResourceWordMap,
} from '@cast/split-me/constants';
import {
  LowConfidenceLookBackMetric,
  LowConfidenceLastRecommendationMetric,
  LowConfidenceTooltip,
  ChartWithPrediction,
  useCpuChartData,
  WorkloadContainerSelect,
  useWorkloadSelectedContainer,
  nonPredictionStrokeDasharrayMap,
  percentileToColor,
  percentileToTitle,
  NoChangeReason,
  getRecommendationNoChangeReason,
} from '@cast/split-me/features/cluster/workload-optimization';

import {
  AutoscalingLegend,
  MetricLegendItem,
} from '../_components/AutoscalingLegend';
import { ChartWithRightLegendCard } from '../../../_components/ui/ChartWithRightLegendCard';
import { useInventoryWorkloadsTimeRangeSelection } from '../../../_hooks/useInventoryWorkloadsTimeRangeSelection';

const CPU_UNIT = computeResourceUnitsMap[ComputeResourceType.CPU];
const CPU_WORD = computeResourceWordMap[ComputeResourceType.CPU];

type UsageMetricKey = 'max' | 'p75' | 'p50' | 'min';

const USAGE_KEYS: UsageMetricKey[] = ['max', 'p75', 'p50', 'min'];

export const CpuAutoscalingChart = () => {
  const { timeZone } = useInventoryWorkloadsTimeRangeSelection();
  const { container } = useWorkloadSelectedContainer();
  const {
    workload,
    isFetching,
    error,
    refetch,
    chartData,
    toggleMetric,
    isMetricDisabled,
  } = useCpuChartData({ container, timeZone });

  const isLoading = isFetching || !workload || !chartData;

  if (error && !isFetching) {
    return (
      <ChartWithRightLegendCard
        testId="cpu-autoscaling-chart"
        title="CPU autoscaling"
        headerEndContent={<WorkloadContainerSelect />}
        chart={
          <FailedToLoad
            title="Failed to load CPU autoscaling"
            refresh={refetch}
          />
        }
        legend={null}
      />
    );
  }

  const requested = chartData?.requested;
  const recommended = chartData?.recommended;
  const isReadOnly = chartData?.isReadOnly ?? false;
  const recommendation = workload?.recommendation;
  const cpuConfig = workload?.workloadConfigV2?.vpaConfig?.cpu;

  const roundedRequested = requested ? round(requested, 3) : 'N/A';
  const roundedRecommended = recommended ? round(recommended, 3) : '--.--';
  const isEqual = roundedRequested === roundedRecommended;

  const requestedColor = isEqual
    ? tokens.theme.data.green500
    : !requested
    ? tokens.theme.data.grey300
    : undefined;

  const recommendedColor = isEqual
    ? isReadOnly
      ? tokens.theme.data.green300
      : tokens.theme.data.green500
    : isReadOnly
    ? tokens.theme.data.grey300
    : undefined;

  const recommendedValueSx = isReadOnly
    ? {
        textDecoration: 'underline dashed',
        textUnderlinePosition: 'under',
        textDecorationColor: tokens.theme.fg.structural.secondary,
      }
    : undefined;

  const differenceValue =
    typeof requested === 'number' && typeof recommended === 'number'
      ? round(requested - recommended, 3)
      : '--.--';

  const differenceDirection =
    typeof differenceValue === 'number' && differenceValue < 0 ? 'up' : 'down';

  const recommendedWarningTooltip = recommendation?.lowConfidence ? (
    <LowConfidenceTooltip
      recommendation={recommendation}
      tooltipPlacement="top"
      content={
        <>
          <LowConfidenceLookBackMetric
            resourceConfig={cpuConfig}
            type={ComputeResourceType.CPU}
          />
          <LowConfidenceLastRecommendationMetric
            recommendation={recommendation}
            type={ComputeResourceType.CPU}
          />
        </>
      }
    >
      <Box display="flex">
        <Icons.WarningCircle
          weight="fill"
          size={16}
          color={tokens.theme.data.yellow500}
        />
      </Box>
    </LowConfidenceTooltip>
  ) : undefined;

  const usageMetrics: MetricLegendItem[] = USAGE_KEYS.map((key) => ({
    id: key,
    label: percentileToTitle[key],
    value: `${dashesOrNum({
      value: chartData?.aggregated?.[key],
      roundBy: 3,
    })}`,
    markerColor: percentileToColor[key],
    onClick: () => toggleMetric(key),
    disabled: isMetricDisabled(key),
  }));

  if (chartData?.hasStall) {
    usageMetrics.push({
      id: 'avgStall',
      label: percentileToTitle.avgStall,
      value: `${dashesOrNum({
        value: chartData?.aggregated?.avgStall,
        roundBy: 3,
      })}`,
      markerColor: percentileToColor.avgStall,
      onClick: () => toggleMetric('avgStall'),
      disabled: isMetricDisabled('avgStall'),
      dimmed: true,
    });
  }

  return (
    <ChartWithRightLegendCard
      testId="cpu-autoscaling-chart"
      title="CPU autoscaling"
      headerEndContent={<WorkloadContainerSelect />}
      isListLoading={isLoading}
      chart={
        <Box flex={1}>
          <ChartWithPrediction
            timeZone={timeZone}
            unit="CPU"
            analyzeDescription="CPU autoscaling with prediction"
            data={chartData?.chartData ?? []}
            dataKeys={chartData?.chartDataKeys ?? []}
            colorMap={percentileToColor}
            tooltipTitleMap={percentileToTitle}
            strokeDasharrayMap={
              chartData?.isReadOnly ? nonPredictionStrokeDasharrayMap : {}
            }
            isLoading={isLoading}
            hasError={!!error && !isFetching}
            refetch={refetch}
            isMetricDisabled={isMetricDisabled}
            dataKeysRight={chartData?.hasStall ? ['avgStall'] : []}
          />
        </Box>
      }
      legend={
        <AutoscalingLegend
          testId="cpu-autoscaling-legend"
          requested={`${roundedRequested}`}
          requestedValueColor={requestedColor}
          requestedOnClick={() => toggleMetric('req')}
          requestedDisabled={isMetricDisabled('req')}
          recommended={`${roundedRecommended}`}
          recommendedMarkerColor={percentileToColor.rec}
          recommendedValueColor={recommendedColor}
          recommendedValueSx={recommendedValueSx}
          recommendedOnClick={() => toggleMetric('rec')}
          recommendedDisabled={isMetricDisabled('rec')}
          recommendedRowTooltip={
            isReadOnly
              ? `This recommendation won't be applied because ${CPU_WORD} optimization is disabled.`
              : undefined
          }
          recommendedWarningTooltip={recommendedWarningTooltip}
          recommendedNoChangeTooltip={
            !recommendation?.lowConfidence &&
            !isReadOnly &&
            getRecommendationNoChangeReason({
              requested,
              recommended,
              config: cpuConfig,
            }) ? (
              <NoChangeReason
                requested={requested}
                recommended={recommended}
                config={cpuConfig}
              />
            ) : undefined
          }
          difference={
            typeof differenceValue === 'number'
              ? `${differenceValue} ${CPU_UNIT}`
              : differenceValue
          }
          differenceDirection={differenceDirection}
          metrics={usageMetrics}
        />
      }
    />
  );
};
